import React from "react";
import { BaseFullScreenTakeover } from "../../Components/BaseFullScreenTakeover";
import { PaymentPrefCreationForAccountsWorkflow } from "../../Workflows/PaymentPrefCreationForAccountsWorkflow";
import { PaymentPrefForAccountsContainerProps } from "./types";

/**
 * A container component that provides a full-screen takeover interface for managing payment preferences
 * across multiple accounts simultaneously. It wraps the PaymentPrefCreationForAccountsWorkflow
 * component in a BaseFullScreenTakeover. This is the main component for the P2.0 payment preference
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {Array<Account>} props.accounts - Array of accounts to update payment preferences for
 * @param {() => void} props.onExit - Callback function when the user exits the workflow
 * @param {() => void} props.onSuccess - Callback function when payment preferences are successfully updated
 *
 * @example
 * ```tsx
 * <PaymentPrefForAccountsContainer
 *   accounts={[
 *     { id: "123", name: "Account 1" },
 *     { id: "456", name: "Account 2" }
 *   ]}
 *   onExit={() => navigate(-1)}
 *   onSuccess={() => refetchData()}
 * />
 * ```
 *
 * @remarks
 * - Uses BaseFullScreenTakeover to provide a modal-like interface
 * - Allows batch updating of payment preferences for multiple accounts at once
 * - Provides a consistent title "Update payment method" in the header
 */
export const PaymentPrefForAccountsContainer = ({
  accounts,
  onExit,
  onSuccess,
}: PaymentPrefForAccountsContainerProps) => {
  return (
    <BaseFullScreenTakeover title="Update payment method" onExit={onExit}>
      <PaymentPrefCreationForAccountsWorkflow
        accounts={accounts}
        onSuccess={onSuccess}
        onExit={onExit}
      />
    </BaseFullScreenTakeover>
  );
};

export type { PaymentPrefForAccountsContainerProps };
